import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import { showErrorToast } from '../../../shared/utils/toastManager';

import {
  FETCH_ACCOUNT_MAXES_START,
  FETCH_ACCOUNT_MAXES_FULFILLED,
  FETCH_ACCOUNT_MAXES_REJECTED,
  FETCH_ACCOUNT_GOALS_START,
  FETCH_ACCOUNT_GOALS_FULFILLED,
  FETCH_ACCOUNT_GOALS_REJECTED,
  FETCH_GROUPS_START,
  FETCH_GROUPS_FULFILLED,
  FETCH_GROUPS_REJECTED,
  FETCH_CALENDARS_START,
  FETCH_CALENDARS_FULFILLED,
  FETCH_CALENDARS_REJECTED,
  FETCH_ATHLETES_START,
  FETCH_ATHLETES_FULFILLED,
  FETCH_ATHLETES_REJECTED,
  FETCH_EXERCISES_START,
  FETCH_EXERCISES_FULFILLED,
  FETCH_EXERCISES_REJECTED,
  CREATE_MAX_EFFORT_START,
  CREATE_MAX_EFFORT_FULFILLED,
  CREATE_MAX_EFFORT_REJECTED,
  SAVE_GOAL_START,
  SAVE_GOAL_FULFILLED,
  SAVE_GOAL_REJECTED,
  DELETE_GOALS_START,
  DELETE_GOALS_FULFILLED,
  DELETE_GOALS_REJECTED,
  FETCH_MAX_HISTORY_START,
  FETCH_MAX_HISTORY_FULFILLED,
  FETCH_MAX_HISTORY_REJECTED,
  FETCH_HISTORY_CURRENT_MAX_START,
  FETCH_HISTORY_CURRENT_MAX_FULFILLED,
  FETCH_HISTORY_CURRENT_MAX_REJECTED,
  SET_MAX_ACTIVE_START,
  SET_MAX_ACTIVE_FULFILLED,
  SET_MAX_ACTIVE_REJECTED,
  ARCHIVE_MAX_START,
  ARCHIVE_MAX_FULFILLED,
  ARCHIVE_MAX_REJECTED,
  SET_MAXES_LOCK_START,
  SET_MAXES_LOCK_FULFILLED,
  SET_MAXES_LOCK_REJECTED,
  RESET_MAXES_START,
  RESET_MAXES_FULFILLED,
  RESET_MAXES_REJECTED,
  BULK_ARCHIVE_MAXES_START,
  BULK_ARCHIVE_MAXES_FULFILLED,
  BULK_ARCHIVE_MAXES_REJECTED,
  SET_MAXES_GOALS_FILTER,
} from './actionTypes';

const getUserPath = (accountCode, userId, isMe) => (
  isMe ? `/me/accounts/${accountCode}` : `/accounts/${accountCode}/users/${userId}`
);

/* * Maxes and Goals * */

export const fetchAccountMaxes = (accountCode, {
  filterType, filterIds = [], exerciseId, type,
} = {}) => (
  (dispatch) => {
    dispatch({ type: FETCH_ACCOUNT_MAXES_START });

    const filterIdsQuery = filterIds.map((id) => `&filterIds=${id}`).join('');
    const exerciseIdQuery = exerciseId ? `&exerciseIds=${exerciseId}` : '';
    const typeQuery = type ? `&type=${type}` : '';

    return axiosAuthed.get(`/accounts/${accountCode}/maxes?filterType=${filterType}${filterIdsQuery}${exerciseIdQuery}${typeQuery}`)
      .then((response) => {
        dispatch({ type: FETCH_ACCOUNT_MAXES_FULFILLED, payload: response.data });
      })
      .catch((err) => {
        dispatch({ type: FETCH_ACCOUNT_MAXES_REJECTED, payload: err });
        showErrorToast('Unable to load maxes. Please try again.');
      });
  }
);

export const fetchAccountGoals = (accountCode, {
  filterType, filterIds = [], exerciseId, type,
} = {}) => (
  (dispatch) => {
    dispatch({ type: FETCH_ACCOUNT_GOALS_START });

    const filterIdsQuery = filterIds.map((id) => `&filterIds=${id}`).join('');
    const exerciseIdQuery = exerciseId ? `&exerciseId=${exerciseId}` : '';
    const typeQuery = type ? `&type=${type}` : '';

    return axiosAuthed.get(`/accounts/${accountCode}/goals?filterType=${filterType}${filterIdsQuery}${exerciseIdQuery}${typeQuery}`)
      .then((response) => {
        dispatch({ type: FETCH_ACCOUNT_GOALS_FULFILLED, payload: response.data });
      })
      .catch((err) => {
        dispatch({ type: FETCH_ACCOUNT_GOALS_REJECTED, payload: err });
        showErrorToast('Unable to load goals. Please try again.');
      });
  }
);

export const fetchMyMaxes = (accountCode, userId, { exerciseId } = {}) => (
  (dispatch) => {
    dispatch({ type: FETCH_ACCOUNT_MAXES_START });
    return axiosAuthed.get(`/me/accounts/${accountCode}/exercises/maxes`)
      .then((response) => {
        const maxEfforts = exerciseId
          ? response.data.filter((effort) => effort.exerciseId === exerciseId)
          : response.data;
        dispatch({ type: FETCH_ACCOUNT_MAXES_FULFILLED, payload: [{ userId, maxEfforts }] });
      })
      .catch((err) => {
        dispatch({ type: FETCH_ACCOUNT_MAXES_REJECTED, payload: err });
        showErrorToast('Unable to load maxes. Please try again.');
      });
  }
);

export const fetchMyGoals = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_ACCOUNT_GOALS_START });
    return axiosAuthed.get(`/me/accounts/${accountCode}/goals`)
      .then((response) => {
        dispatch({ type: FETCH_ACCOUNT_GOALS_FULFILLED, payload: response.data });
      })
      .catch((err) => {
        dispatch({ type: FETCH_ACCOUNT_GOALS_REJECTED, payload: err });
        showErrorToast('Unable to load goals. Please try again.');
      });
  }
);

/* * Filter Options * */

export const fetchGroups = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_GROUPS_START });
    return axiosAuthed.get(`/accounts/${accountCode}/groups`)
      .then((response) => {
        dispatch({ type: FETCH_GROUPS_FULFILLED, payload: response.data });
      })
      .catch((err) => {
        dispatch({ type: FETCH_GROUPS_REJECTED, payload: err });
        showErrorToast('Unable to load groups. Please try again.');
      });
  }
);

export const fetchCalendars = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_CALENDARS_START });
    return axiosAuthed.get(`/accounts/${accountCode}/navigator/calendar`)
      .then((response) => {
        const { activeCalendars = [], inactiveCalendars = [] } = response.data || {};
        dispatch({
          type: FETCH_CALENDARS_FULFILLED,
          payload: [...activeCalendars, ...inactiveCalendars],
        });
      })
      .catch((err) => {
        dispatch({ type: FETCH_CALENDARS_REJECTED, payload: err });
        showErrorToast('Unable to load calendars. Please try again.');
      });
  }
);

export const fetchAthletes = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_ATHLETES_START });
    return axiosAuthed.get(`/accounts/${accountCode}/users?userType=0`)
      .then((response) => {
        dispatch({ type: FETCH_ATHLETES_FULFILLED, payload: response.data });
      })
      .catch((err) => {
        dispatch({ type: FETCH_ATHLETES_REJECTED, payload: err });
        showErrorToast('Unable to load athletes. Please try again.');
      });
  }
);

export const fetchExercises = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_EXERCISES_START });
    return axiosAuthed.get(`/accounts/${accountCode}/exercises?tracked=1&includeHealthItems=true`)
      .then((response) => {
        dispatch({ type: FETCH_EXERCISES_FULFILLED, payload: response.data });
      })
      .catch((err) => {
        dispatch({ type: FETCH_EXERCISES_REJECTED, payload: err });
        showErrorToast('Unable to load exercises. Please try again.');
      });
  }
);

/* * Adding Maxes and Goals * */

export const createMaxEfforts = (accountCode, exerciseId, items, { onSuccess } = {}) => (
  (dispatch) => {
    dispatch({ type: CREATE_MAX_EFFORT_START });
    return axiosAuthed.post(`/accounts/${accountCode}/exercises/${exerciseId}/maxes`, items)
      .then((response) => {
        dispatch({ type: CREATE_MAX_EFFORT_FULFILLED, payload: response.data });
        if (onSuccess) onSuccess(response.data);
      })
      .catch((err) => {
        dispatch({ type: CREATE_MAX_EFFORT_REJECTED, payload: err });
        showErrorToast(err?.response?.headers?.message || 'Unable to add maxes. Please try again.');
      });
  }
);

export const createMyMaxEffort = (accountCode, exerciseId, item, { onSuccess } = {}) => (
  (dispatch) => {
    dispatch({ type: CREATE_MAX_EFFORT_START });
    return axiosAuthed.post(`/me/accounts/${accountCode}/exercises/${exerciseId}/maxes`, item)
      .then((response) => {
        dispatch({ type: CREATE_MAX_EFFORT_FULFILLED, payload: response.data });
        if (onSuccess) onSuccess(response.data);
      })
      .catch((err) => {
        dispatch({ type: CREATE_MAX_EFFORT_REJECTED, payload: err });
        showErrorToast(err?.response?.headers?.message || 'Unable to add max. Please try again.');
      });
  }
);

export const saveGoal = (accountCode, body, { onSuccess } = {}) => (
  (dispatch) => {
    dispatch({ type: SAVE_GOAL_START });
    return axiosAuthed.post(`/accounts/${accountCode}/goals`, body)
      .then((response) => {
        dispatch({ type: SAVE_GOAL_FULFILLED, payload: response.data });
        if (onSuccess) onSuccess(response.data);
      })
      .catch((err) => {
        dispatch({ type: SAVE_GOAL_REJECTED, payload: err });
        showErrorToast(err?.response?.headers?.message || 'Unable to save goal. Please try again.');
      });
  }
);

export const deleteGoals = (accountCode, goalIds, { onSuccess } = {}) => (
  (dispatch) => {
    dispatch({ type: DELETE_GOALS_START });
    return axiosAuthed.patch(`/accounts/${accountCode}/goals`, { goalIds })
      .then((response) => {
        dispatch({ type: DELETE_GOALS_FULFILLED, payload: response.data });
        if (onSuccess) onSuccess(response.data);
      })
      .catch((err) => {
        dispatch({ type: DELETE_GOALS_REJECTED, payload: err });
        showErrorToast('Unable to remove goal. Please try again.');
      });
  }
);

/* * Max History * */

export const fetchMaxHistory = (accountCode, userId, exerciseId, options = {}) => (
  (dispatch) => {
    dispatch({ type: FETCH_MAX_HISTORY_START });
    const { limit, dateRangeStart, isMe } = options;
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (dateRangeStart) params.set('dateRangeStart', dateRangeStart);
    const query = params.toString() ? `?${params.toString()}` : '';
    return axiosAuthed.get(`${getUserPath(accountCode, userId, isMe)}/exercises/${exerciseId}/maxes/history${query}`)
      .then((response) => {
        dispatch({ type: FETCH_MAX_HISTORY_FULFILLED, payload: response.data });
      })
      .catch((err) => {
        dispatch({ type: FETCH_MAX_HISTORY_REJECTED, payload: err });
        showErrorToast('Unable to load max history. Please try again.');
      });
  }
);

export const fetchHistoryCurrentMax = (accountCode, userId, exerciseId, { isMe } = {}) => (
  (dispatch) => {
    dispatch({ type: FETCH_HISTORY_CURRENT_MAX_START });
    return axiosAuthed.get(`${getUserPath(accountCode, userId, isMe)}/exercises/${exerciseId}/maxes`)
      .then((response) => {
        dispatch({ type: FETCH_HISTORY_CURRENT_MAX_FULFILLED, payload: response.data });
      })
      .catch((err) => {
        dispatch({ type: FETCH_HISTORY_CURRENT_MAX_REJECTED, payload: err });
        showErrorToast('Unable to load current max. Please try again.');
      });
  }
);

export const setMaxActive = (accountCode, userId, exerciseId, maxId, { onSuccess, isMe } = {}) => (
  (dispatch) => {
    dispatch({ type: SET_MAX_ACTIVE_START });
    return axiosAuthed.put(`${getUserPath(accountCode, userId, isMe)}/exercises/${exerciseId}/maxes/${maxId}/active`)
      .then((response) => {
        dispatch({ type: SET_MAX_ACTIVE_FULFILLED, payload: response.data });
        if (onSuccess) onSuccess(response.data);
      })
      .catch((err) => {
        dispatch({ type: SET_MAX_ACTIVE_REJECTED, payload: err });
        showErrorToast(err?.response?.headers?.message || 'Unable to set current max. Please try again.');
      });
  }
);

export const archiveMax = (accountCode, userId, exerciseId, maxId, { onSuccess, isMe } = {}) => (
  (dispatch) => {
    dispatch({ type: ARCHIVE_MAX_START });
    return axiosAuthed.put(`${getUserPath(accountCode, userId, isMe)}/exercises/${exerciseId}/maxes/${maxId}/archive`)
      .then((response) => {
        dispatch({ type: ARCHIVE_MAX_FULFILLED, payload: response.data });
        if (onSuccess) onSuccess(response.data);
      })
      .catch((err) => {
        dispatch({ type: ARCHIVE_MAX_REJECTED, payload: err });
        showErrorToast(err?.response?.headers?.message || 'Unable to delete max. Please try again.');
      });
  }
);

/* * Bulk Actions * */

export const setMaxesLock = (accountCode, body, isLocking, { onSuccess } = {}) => (
  (dispatch) => {
    dispatch({ type: SET_MAXES_LOCK_START });
    return axiosAuthed.patch(`/accounts/${accountCode}/maxes/${isLocking ? 'lock' : 'unlock'}`, body)
      .then((response) => {
        dispatch({ type: SET_MAXES_LOCK_FULFILLED, payload: response.data });
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        dispatch({ type: SET_MAXES_LOCK_REJECTED, payload: err });
        showErrorToast(err?.response?.headers?.message || 'Unable to update lock. Please try again.');
      });
  }
);

export const resetMaxes = (accountCode, body, { onSuccess } = {}) => (
  (dispatch) => {
    dispatch({ type: RESET_MAXES_START });
    return axiosAuthed.patch(`/accounts/${accountCode}/maxes/reset`, body)
      .then(() => {
        dispatch({ type: RESET_MAXES_FULFILLED });
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        dispatch({ type: RESET_MAXES_REJECTED, payload: err });
        showErrorToast(err?.response?.headers?.message || 'Unable to reset maxes. Please try again.');
      });
  }
);

export const bulkArchiveMaxes = (accountCode, body, { onSuccess } = {}) => (
  (dispatch) => {
    dispatch({ type: BULK_ARCHIVE_MAXES_START });
    return axiosAuthed.patch(`/accounts/${accountCode}/maxes/bulk-archive`, body)
      .then((response) => {
        dispatch({ type: BULK_ARCHIVE_MAXES_FULFILLED, payload: response.data });
        if (onSuccess) onSuccess(response.data);
      })
      .catch((err) => {
        dispatch({ type: BULK_ARCHIVE_MAXES_REJECTED, payload: err });
        showErrorToast(err?.response?.headers?.message || 'Unable to delete maxes. Please try again.');
      });
  }
);

export const setMaxesGoalsFilter = (filter) => ({
  type: SET_MAXES_GOALS_FILTER,
  payload: filter,
});
